// Voice/video calls over a self-hosted LiveKit SFU, end-to-end encrypted with a key the
// conversation's MLS group exports for its current epoch (re-keyed on every membership
// change). The SFU only ever forwards frames it cannot decrypt.
import { defineStore } from 'pinia'
import { computed, markRaw, ref, shallowRef, watch } from 'vue'
import type { Room as RoomT, Participant, Track, RemoteTrack } from 'livekit-client'
import type { RingMode, Uuid } from '../api/types'
import { fromB64url } from '../lib/attachments'
import type { AvKind } from '../lib/avDevices'
import { appInvoke, isApp } from '../platform'
import { useAv } from './av'
import { useSession } from './session'

export interface CallMember {
  /** LiveKit identity: `user/device`, so the same person on two devices is two members. */
  identity: string
  /** The account, for avatars and profiles. */
  userId: string
  name: string
  local: boolean
  speaking: boolean
  micOn: boolean
  camera: Track | null
  screen: Track | null
}

/** One thing on screen: a person's camera (or avatar), or their screen share. */
export interface CallTile {
  key: string
  member: CallMember
  kind: 'camera' | 'screen'
  track: Track | null
}

const CALL_HEARTBEAT_MS = 20_000
const SPEAKER_KEY = 'hrth.call-speaker'

/** The Android app can play calls on the earpiece (apps/tauri/plugins/call-audio); browsers
 * and the other apps can't choose. */
export const canRouteAudio = isApp && /Android/i.test(globalThis.navigator?.userAgent ?? '')

export const useCall = defineStore('call', () => {
  const conv = ref<Uuid | null>(null)
  const state = ref<'idle' | 'connecting' | 'connected'>('idle')
  const members = shallowRef<CallMember[]>([])
  const e2ee = ref(false)
  const mic = ref(true)
  const camera = ref(false)
  const screen = ref(false)
  /** Android app: the loudspeaker rather than the earpiece; remembered between calls. */
  const speaker = ref(localStorage.getItem(SPEAKER_KEY) !== 'earpiece')
  /** Stage view: the call fills the conversation area instead of a strip above chat. */
  const expanded = ref(false)
  const chatOpen = ref(true)
  /** Tile the viewer pinned; '' means they unpinned on purpose (no auto-focus). */
  const pin = ref<string | null>(null)
  const room = shallowRef<RoomT | null>(null)
  const audioSink = document.createElement('div')
  audioSink.style.display = 'none'
  audioSink.dataset.testid = 'call-audio'
  document.body.appendChild(audioSink)
  let keyProvider: { setKey(k: ArrayBuffer): Promise<void> } | null = null
  let keyEpoch = -1
  let unsub: (() => void) | null = null
  /** Tells the server (and so the conversation) we're still in the call. */
  let heartbeat: ReturnType<typeof setInterval> | null = null
  /** Bumped by every join and leave: a join that finds it changed was left meanwhile. */
  let joins = 0
  /** The microphone this call opened. Leaving must close it: while a page holds a Bluetooth
   * headset's mic, the system keeps the headset in call quality, for everything. */
  let micTrack: MediaStreamTrack | null = null
  /** Everything else captured while a call is up, LiveKit's own included: it reopens the mic
   * by itself when the track ends (a Bluetooth link dropping does that), and one still
   * opening when we leave would otherwise outlive the call. */
  const captured = new Set<MediaStreamTrack>()
  let capturing = false
  let captureGen = 0
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
  const getUserMedia = md?.getUserMedia?.bind(md)
  if (md && getUserMedia) {
    md.getUserMedia = async (c?: MediaStreamConstraints) => {
      const gen = capturing ? captureGen : -1
      const stream = await getUserMedia(c)
      if (gen < 0) return stream
      for (const t of stream.getTracks()) {
        if (capturing && gen === captureGen) captured.add(t)
        else t.stop() // the call ended while this was opening
      }
      return stream
    }
  }

  function refresh(): void {
    const r = room.value
    if (!r) {
      members.value = []
      return
    }
    const all: Participant[] = [r.localParticipant, ...r.remoteParticipants.values()]
    members.value = all.map((p) => {
      const pubs = [...p.trackPublications.values()]
      const find = (source: string) => pubs.find((t) => t.source === source && t.track && !t.isMuted)?.track ?? null
      return {
        identity: p.identity,
        userId: p.identity.split('/')[0] ?? p.identity,
        name: p.name || p.identity.split('@')[0] || p.identity,
        local: p === r.localParticipant,
        speaking: p.isSpeaking,
        micOn: p.isMicrophoneEnabled,
        camera: find('camera') ? markRaw(find('camera')!) : null,
        screen: find('screen_share') ? markRaw(find('screen_share')!) : null,
      }
    })
  }

  const tiles = computed<CallTile[]>(() =>
    members.value.flatMap((m) => {
      const cam: CallTile = { key: m.identity, member: m, kind: 'camera', track: m.camera }
      return m.screen ? [{ key: `${m.identity}#screen`, member: m, kind: 'screen' as const, track: m.screen }, cam] : [cam]
    }),
  )

  /** The tile shown large: the viewer's pin, else someone else's screen share. */
  const focused = computed<CallTile | null>(() => {
    if (pin.value === '') return null
    return (
      tiles.value.find((t) => t.key === pin.value) ??
      tiles.value.find((t) => t.kind === 'screen' && !t.member.local) ??
      null
    )
  })

  // A new remote screen share opens the stage and takes focus, even after an unpin.
  let shares = new Set<string>()
  watch(tiles, (ts) => {
    const now = new Set(ts.filter((t) => t.kind === 'screen' && !t.member.local).map((t) => t.key))
    const fresh = [...now].find((k) => !shares.has(k))
    shares = now
    if (!fresh) return
    if (pin.value === '') pin.value = null
    expanded.value = true
  })

  // Opening the stage on a wide screen keeps chat alongside; on phones the stage gets the screen.
  watch(expanded, (on) => {
    if (on) chatOpen.value = window.matchMedia('(min-width: 1024px)').matches
  })

  function togglePin(key: string): void {
    pin.value = focused.value?.key === key ? '' : key
    if (pin.value) expanded.value = true
  }

  async function applyKey(): Promise<void> {
    const session = useSession()
    if (!conv.value || !keyProvider) return
    const k = session.engine?.callKey(conv.value)
    if (!k || k.epoch === keyEpoch) return
    keyEpoch = k.epoch
    await keyProvider.setKey(fromB64url(k.key).buffer)
  }

  /** Joins `c`'s call; starting it rings or notifies the others by `ring` (default notify). */
  async function join(c: Uuid, ring?: RingMode): Promise<void> {
    // iOS Safari grants the microphone only inside the tap that asked for it, so ask
    // before any await (loading LiveKit, the token and connecting take too long).
    const av = useAv()
    const micMedia = microphone(av.current('audioinput'))
    micMedia.catch(() => undefined) // surfaces below, once awaited
    if (state.value !== 'idle') await leave()
    const mine = ++joins
    capturing = true
    // Left (or joined elsewhere) while this was connecting: close what this join opened,
    // without touching the call state that now belongs to someone else.
    const abandon = (r?: { disconnect(): Promise<void> }) => {
      void micMedia.then(({ track }) => track.stop()).catch(() => undefined)
      if (r) void r.disconnect().catch(() => undefined)
    }
    const session = useSession()
    state.value = 'connecting'
    conv.value = c
    try {
      const { Room, RoomEvent, ExternalE2EEKeyProvider, LocalAudioTrack, Track, AudioPresets } = await import('livekit-client')
      const t = await session.api.callToken(c)
      if (mine !== joins) return abandon()
      const kp = new ExternalE2EEKeyProvider()
      keyProvider = kp
      keyEpoch = -1
      const worker = new Worker(new URL('livekit-client/e2ee-worker', import.meta.url), { type: 'module' })
      await av.refresh()
      if (mine !== joins) return abandon()
      const r = new Room({
        adaptiveStream: true,
        dynacast: true,
        e2ee: { keyProvider: kp, worker },
        audioCaptureDefaults: { deviceId: av.current('audioinput') },
        videoCaptureDefaults: { deviceId: av.current('videoinput') },
        audioOutput: { deviceId: av.current('audiooutput') },
        // LiveKit's default is 48 kbps; voices sounded thin.
        publishDefaults: { audioPreset: AudioPresets.musicHighQuality },
      })
      room.value = markRaw(r)
      await applyKey()
      await r.setE2EEEnabled(true)
      for (const ev of [
        RoomEvent.ParticipantConnected,
        RoomEvent.ParticipantDisconnected,
        RoomEvent.TrackSubscribed,
        RoomEvent.TrackUnsubscribed,
        RoomEvent.TrackPublished,
        RoomEvent.TrackUnpublished,
        RoomEvent.TrackMuted,
        RoomEvent.TrackUnmuted,
        RoomEvent.LocalTrackPublished,
        RoomEvent.LocalTrackUnpublished,
        RoomEvent.ActiveSpeakersChanged,
      ]) {
        r.on(ev, refresh)
      }
      r.on(RoomEvent.TrackSubscribed, (track: RemoteTrack) => {
        if (track.kind !== 'audio') return
        const el = track.attach() as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
        const out = useAv().current('audiooutput')
        if (out && el.setSinkId) void el.setSinkId(out).catch(() => undefined)
        audioSink.appendChild(el)
      })
      r.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => track.detach().forEach((el) => el.remove()))
      r.on(RoomEvent.ParticipantEncryptionStatusChanged, () => (e2ee.value = r.isE2EEEnabled))
      r.on(RoomEvent.Disconnected, () => {
        if (room.value === r) void cleanup()
      })
      // Membership changes advance the MLS epoch: rotate the media key with them.
      unsub = session.engine?.on((ev) => {
        if (ev.type === 'membership' && ev.conv === c) void applyKey()
      }) ?? null
      await r.connect(t.url, t.token)
      const { track, constraints } = await micMedia
      if (mine !== joins) return abandon(r)
      micTrack = track
      // Not user-provided, so LiveKit can reacquire it when switching microphones.
      await r.localParticipant.publishTrack(new LocalAudioTrack(track, constraints, false), { source: Track.Source.Microphone })
      mic.value = true
      // After the microphone opens: the WebView picks the loudspeaker as it does.
      if (canRouteAudio) void route()
      e2ee.value = r.isE2EEEnabled
      state.value = 'connected'
      refresh()
      let first: RingMode | undefined = ring
      const beat = () =>
        session.api
          .callPresence(c, true, first)
          .finally(() => (first = undefined))
          .then(({ participants }) => (session.inCall = { ...session.inCall, [c]: participants }))
          .catch(() => undefined)
      void beat()
      heartbeat = setInterval(beat, CALL_HEARTBEAT_MS)
    } catch (e) {
      void micMedia.then(({ track }) => track.stop()).catch(() => undefined)
      if (mine !== joins) return
      await cleanup()
      throw e
    }
  }

  /** Opens the microphone, falling back to the default one when the chosen one is gone. */
  async function microphone(id: string | undefined): Promise<{ track: MediaStreamTrack; constraints: MediaTrackConstraints }> {
    // Not through the wrapper above: this is asked before a previous call is left (iOS),
    // whose cleanup would close it; `micTrack` and `abandon` look after it instead.
    if (!getUserMedia) throw new Error('This browser can’t use a microphone here (calls need HTTPS)')
    const want: MediaTrackConstraints = id ? { deviceId: { exact: id } } : {}
    const stream = await getUserMedia({ audio: want })
      .catch((e: unknown) => {
        if (!id || (e as Error).name !== 'OverconstrainedError') throw e
        return getUserMedia({ audio: true })
      })
      .catch((e: unknown) => {
        if ((e as Error).name === 'NotAllowedError') throw new Error('Microphone access was blocked. Allow it for this site in your browser settings.')
        throw e
      })
    const track = stream.getAudioTracks()[0]
    if (!track) throw new Error('No microphone found')
    return { track, constraints: track.getSettings().deviceId ? { deviceId: track.getSettings().deviceId } : {} }
  }

  async function cleanup(): Promise<void> {
    joins++
    capturing = false
    captureGen++
    unsub?.()
    unsub = null
    if (heartbeat) {
      clearInterval(heartbeat)
      heartbeat = null
      const left = conv.value
      if (left) void useSession().api.callPresence(left, false).catch(() => undefined)
    }
    const r = room.value
    room.value = null
    if (r) {
      // Every track this side captured (LiveKit reopens the mic itself when a device
      // changes, so the one we opened may not be the one live now).
      for (const p of r.localParticipant.trackPublications.values()) p.track?.stop()
      await r.disconnect().catch(() => undefined)
    }
    micTrack?.stop()
    micTrack = null
    for (const t of captured) t.stop()
    captured.clear()
    audioSink.replaceChildren()
    if (canRouteAudio) void appInvoke('plugin:call-audio|reset').catch(() => undefined)
    state.value = 'idle'
    conv.value = null
    members.value = []
    camera.value = false
    screen.value = false
    expanded.value = false
    pin.value = null
    shares = new Set()
    keyProvider = null
  }

  async function leave(): Promise<void> {
    await cleanup()
  }

  /** Moves a live call to another microphone, camera or speaker without rejoining. */
  async function switchDevice(kind: AvKind, id: string): Promise<void> {
    const r = room.value
    if (!r) return
    await r.switchActiveDevice(kind, id).catch(() => undefined)
    if (kind === 'audiooutput') {
      for (const el of audioSink.querySelectorAll('audio')) {
        const a = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
        await a.setSinkId?.(id).catch(() => undefined)
      }
    }
  }

  async function toggleMic(): Promise<void> {
    if (!room.value) return
    mic.value = !mic.value
    await room.value.localParticipant.setMicrophoneEnabled(mic.value)
    refresh()
  }

  async function toggleCamera(): Promise<void> {
    if (!room.value) return
    camera.value = !camera.value
    await room.value.localParticipant.setCameraEnabled(camera.value)
    refresh()
  }

  function route(): Promise<void> {
    return appInvoke<void>('plugin:call-audio|route', { speaker: speaker.value }).catch(() => undefined)
  }

  async function toggleSpeaker(): Promise<void> {
    speaker.value = !speaker.value
    localStorage.setItem(SPEAKER_KEY, speaker.value ? 'speaker' : 'earpiece')
    if (room.value) await route()
  }

  async function toggleScreen(): Promise<void> {
    if (!room.value) return
    try {
      screen.value = !screen.value
      await room.value.localParticipant.setScreenShareEnabled(screen.value)
    } catch {
      screen.value = false
    }
    refresh()
  }

  return {
    conv,
    state,
    members,
    tiles,
    focused,
    expanded,
    chatOpen,
    e2ee,
    mic,
    camera,
    screen,
    join,
    leave,
    toggleMic,
    toggleCamera,
    toggleScreen,
    speaker,
    toggleSpeaker,
    togglePin,
    switchDevice,
  }
})
